import { useAuthStore } from '../store/auth';
import { useToastStore } from '../store/toast';
import type { ApiResponse } from './types';
import { recordUnauthorized } from '../lib/logout-reason';
import { API_FETCH_URL } from './base';

const BASE_URL = API_FETCH_URL;

export class ApiError extends Error {
  public status: number;
  public data?: unknown;

  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * The API refused the token: remember which request did it (the login page
 * shows it), drop the session and go to /login. Exported for the few callers
 * that fetch outside this client.
 */
export async function signOutOnUnauthorized(method: string, url: string, response: Response): Promise<never> {
  let message: string | undefined;
  try {
    const body = await response.clone().json() as { error?: string };
    message = body.error;
  } catch {
    // not JSON
  }
  recordUnauthorized(method, url, message);
  useAuthStore.getState().clearAuth();
  window.location.href = '/login';
  throw new ApiError('Unauthorized', 401);
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  skipAuth?: boolean;
}

async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const { body, skipAuth = false, ...init } = options;

  const headers = new Headers(init.headers);

  // Set content type for JSON body
  if (body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  // Add authorization header if authenticated
  if (!skipAuth) {
    const token = useAuthStore.getState().token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
  }

  const config: RequestInit = {
    ...init,
    headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  const url = endpoint.startsWith('http') ? endpoint : `${BASE_URL}${endpoint}`;

  const response = await fetch(url, config);

  // Handle 401 Unauthorized - logout and redirect. A request sent without the
  // token (the login form) gets its 401 as an ordinary error instead.
  if (response.status === 401 && !skipAuth) {
    await signOutOnUnauthorized(init.method ?? 'GET', url, response);
  }

  // Try to parse JSON response
  let data: ApiResponse<T>;
  try {
    data = await response.json();
  } catch {
    throw new ApiError('Invalid response from server', response.status);
  }

  // Handle error responses
  if (!response.ok || !data.success) {
    throw new ApiError(
      data.error || `Request failed with status ${response.status}`,
      response.status,
      data
    );
  }

  // An action that needs a super admin's approval comes back as 202 with this
  // marker: the thing the caller asked for has NOT happened. Nothing visibly
  // changes, so say why rather than letting it look like a glitch. 202 already
  // passes the success check above, which is exactly why this detection has to
  // be explicit.
  const payload = data.data as unknown as
    { pending_approval?: boolean; kind?: string } | null;
  if (payload && typeof payload === 'object' && payload.pending_approval) {
    useToastStore.getState().push(
      payload.kind === 'keystore'
        ? KEYSTORE_PENDING_TOAST
        : payload.kind === 'reveal'
          ? (endpoint.endsWith('/totp')
            ? 'Viewing the 2FA code needs a super admin approval. A request was sent — press “Code” again once it is approved.'
            : 'Viewing the password needs a super admin approval. A request was sent — press “Show” again once it is approved.')
          : 'Deletion needs a super admin approval. A request was sent — the item stays until it is approved.',
    );
  }

  return data.data as T;
}

/** Shared with saveBuildConfig, whose multipart upload bypasses request(). */
export const KEYSTORE_PENDING_TOAST =
  'Replacing the keystore needs an admin approval. A request was sent — press the button again once it is approved.';

// HTTP method wrappers
export const api = {
  get<T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'GET' });
  },

  post<T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'POST', body });
  },

  put<T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'PUT', body });
  },

  patch<T>(endpoint: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'PATCH', body });
  },

  delete<T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'DELETE' });
  },

  // For file uploads with custom content type
  upload<T>(endpoint: string, file: ArrayBuffer | Blob, contentType: string): Promise<T> {
    const token = useAuthStore.getState().token;
    const headers: HeadersInit = {
      'Content-Type': contentType,
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return fetch(`${BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers,
      body: file,
    }).then(async (response) => {
      if (response.status === 401) {
        await signOutOnUnauthorized('PUT', `${BASE_URL}${endpoint}`, response);
      }

      const data: ApiResponse<T> = await response.json();
      if (!response.ok || !data.success) {
        throw new ApiError(
          data.error || `Upload failed with status ${response.status}`,
          response.status,
          data
        );
      }

      return data.data as T;
    });
  },

  form<T>(endpoint: string, body: FormData, options?: { skipAuth?: boolean }): Promise<T> {
    const token = useAuthStore.getState().token;
    const headers = new Headers();
    if (token && !options?.skipAuth) headers.set('Authorization', `Bearer ${token}`);
    return fetch(`${BASE_URL}${endpoint}`, { method: 'POST', headers, body }).then(async (response) => {
      if (response.status === 401 && !options?.skipAuth) {
        await signOutOnUnauthorized('POST', `${BASE_URL}${endpoint}`, response);
      }
      const data = await response.json() as ApiResponse<T>;
      if (!response.ok || !data.success) {
        throw new ApiError(data.error || `Request failed with status ${response.status}`, response.status, data);
      }
      return data.data as T;
    });
  },

  blob(endpoint: string, options?: { skipAuth?: boolean }): Promise<Blob> {
    const token = useAuthStore.getState().token;
    const headers = new Headers();
    if (token && !options?.skipAuth) headers.set('Authorization', `Bearer ${token}`);
    return fetch(`${BASE_URL}${endpoint}`, { headers }).then(async (response) => {
      if (response.status === 401 && !options?.skipAuth) {
        await signOutOnUnauthorized('GET', `${BASE_URL}${endpoint}`, response);
      }
      if (!response.ok) {
        let message = `Request failed with status ${response.status}`;
        try {
          const data = await response.json() as ApiResponse<unknown>;
          if (data.error) message = data.error;
        } catch {
          // Non-JSON proxy/storage error — keep the status-based message.
        }
        throw new ApiError(message, response.status);
      }
      return response.blob();
    });
  },
};

export default api;
